import React, { useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { fDate, onlyTime } from "src/utils/formatTime";
import {
  statusColorMap,
  statusIconMap,
  Status,
} from "../constants/ocpi.constant";
import { getOcpiRole, getOcpiVersions } from "src/utils/helper";

const PartnerCard = ({
  data,
  onView,
  onConfigure,
  onSync,
  onDelete,
  ocpiRoles,
  ocpiVersions,
}) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, minWidth: 340, maxWidth: 420, boxShadow: 0 }}
    >
      <CardContent>
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {data.partnerName}{" "}
              <Icon
                icon={statusIconMap[data.status].icon}
                color={theme.palette[statusIconMap[data.status].color].main}
                width={20}
              />
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 500, mt: 0.5 }}
            >
              {data.code}
            </Typography>
          </Grid>
          <Grid item>
            <Chip
              label={data.status}
              color={statusColorMap[data.status].color}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: statusColorMap[data.status].bgcolor,
                color: statusColorMap[data.status].text,
              }}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Business Details
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.2 }}>
              {data.partnerName}
            </Typography>
            {data.businessId && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon icon="mdi:web" width={16} />
                <Typography
                  variant="caption"
                  color="primary"
                  component="a"
                  href={data.businessId}
                  target="_blank"
                  rel="noopener"
                  sx={{ textDecoration: "underline" }}
                >
                  Website
                </Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              OCPI Version
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.2 }}>
              {getOcpiVersions(ocpiVersions, data.ocpiVersion)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Role: <b>{getOcpiRole(ocpiRoles, data.ocpiRole)}</b>
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Last Sync
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {data.lastSyncAt ? fDate(data.lastSyncAt) : "Never"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.lastSyncAt ? onlyTime(data.lastSyncAt) : ""}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Connection
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor:
                    data.status === Status.ACTIVE
                      ? "primary.main"
                      : "error.main",
                  mr: 0.5,
                }}
              />
              <Typography
                variant="caption"
                color={
                  data.status === Status.ACTIVE ? "primary.main" : "error.main"
                }
                fontWeight={600}
              >
                {data.status === Status.ACTIVE ? "Connected" : "Disconnected"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Tooltip title="View Partner" placement="top">
            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:eye-outline" />}
              onClick={onView}
              sx={{ flex: 1 }}
            >
              View
            </Button>
          </Tooltip>
          <Tooltip
            title="Go to the onboarding configuration wizard"
            placement="top"
          >
            <Button
              variant="outlined"
              startIcon={<Icon icon="mdi:pencil-outline" />}
              onClick={onConfigure}
              sx={{ flex: 1 }}
            >
              Configure
            </Button>
          </Tooltip>
          <Tooltip title="Sync Partner Data" placement="top">
            <IconButton color="primary" onClick={onSync}>
              <Icon icon="mdi:reload" />
            </IconButton>
          </Tooltip>
          <IconButton color="error" onClick={onDelete}>
            <Icon icon="mdi:delete-outline" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
};
export default PartnerCard;
